import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import styled, { css, CSSProperties } from "styled-components";

interface DocumentTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  leftIcon?: React.ReactNode;
  errorMessage?: string;
  inputStyle?: CSSProperties | undefined;
}

const DocumentTextAreaField = forwardRef<
  HTMLTextAreaElement,
  DocumentTextAreaProps
>(
  (
    { leftIcon, className, style, errorMessage, inputStyle, onChange, ...rest },
    ref,
  ) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => internalRef.current!, []);

    const resizeTextarea = useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea) return;

      // 스크롤 높이를 정확히 측정하기 위해 높이를 초기화
      textarea.style.height = "auto";

      // 현재 스크롤 높이를 가져와서 설정
      const newHeight = Math.max(textarea.scrollHeight, 24); // 최소 높이 24px
      textarea.style.height = `${newHeight}px`;
    }, []);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizeTextarea();
        onChange?.(e);
      },
      [resizeTextarea, onChange],
    );

    // 초기 렌더링과 value 변경 시 높이 조절
    useLayoutEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, rest.value]);

    return (
      <WrapStyle>
        <TextAreaStyle
          className={className}
          style={style}
          error={!!errorMessage}
        >
          {leftIcon}
          <textarea
            style={inputStyle}
            {...rest}
            ref={internalRef}
            onChange={handleChange}
            maxLength={rest.maxLength}
            rows={1}
          />
        </TextAreaStyle>
        {errorMessage && <label>{errorMessage}</label>}
      </WrapStyle>
    );
  },
);
DocumentTextAreaField.displayName = "DocumentTextAreaField";

export default DocumentTextAreaField;

/* 스타일 */
const WrapStyle = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.6rem;

  label {
    padding-left: 1.6rem;
    ${({ theme }) => theme.typography.body3.regular};
    color: ${({ theme }) => theme.color.text.danger};
  }
`;

const TextAreaStyle = styled.div<{ error: boolean }>`
  display: inline-flex;
  padding: 1.6rem;
  align-items: center;
  gap: 0.8rem;
  overflow: hidden;
  width: 100%;
  min-height: 5.6rem;
  box-sizing: border-box;

  border-radius: 0.5rem;
  background: ${({ theme }) => theme.color.background.dark};
  ${({ theme, error }) =>
    error &&
    css`
      border: 0.1rem solid ${theme.color.text.danger};
    `}

  textarea {
    flex: 1;
    width: 100%;

    background: none;
    border: none;
    outline: none;
    padding: 0;
    resize: none;
    font-family: inherit;

    ${({ theme }) => theme.typography.body1.regular}
    letter-spacing: -0.048rem;

    ::placeholder {
      color: ${({ theme }) => theme.color.text.placeholder};
    }
  }

  svg {
    width: 2.4rem;
    height: 2.4rem;
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.icon.tertiary};
  }

  &:hover {
    background: #eceef4;
  }

  ${({ theme }) => theme.media.small} {
    height: 4.4rem;
    padding: 1.3rem 1.6rem;

    textarea {
      ${({ theme }) => theme.typography.body3.regular}
    }
  }
`;
